/**
 * @fileoverview Footer Section - Homepage footer
 *
 * Purpose: Compact site footer with brand wordmark, link columns,
 * copyright, theme switcher, and a demo request CTA.
 *
 * Features:
 * - Small Lora brand wordmark (left)
 * - Grouped link columns (Social, Other) with muted uppercase headers
 * - Copyright notice
 * - Theme switcher
 * - "Request a demo" CTA (opens demo dialog)
 * - Single-row layout on desktop, stacked on mobile
 *
 * Used by: Homepage (index.tsx)
 */

import { useState } from "react";
import { ThemeSwitcherHome } from "@/ui/settings";
import { Button } from "@/ui/primitives";
import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";

/**
 * Footer section component with a compact single-row layout
 */
export function FooterSection() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  return (
    <footer className="relative z-10 w-full border-t border-white/20 bg-[#1a4025]">
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          {/* Brand wordmark */}
          <div className="flex-shrink-0">
            <span
              className="text-2xl font-medium leading-none tracking-tight text-white"
              style={{ fontFamily: "'Lora', serif" }}
            >
              Morgan
            </span>
          </div>

          {/* Link columns */}
          <div className="flex flex-1 flex-wrap items-start justify-start gap-x-12 gap-y-8 sm:gap-x-16 lg:ml-24">
            {/* Other Links */}
            <div>
              <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-white/50">
                Resources
              </h3>
              <ul className="space-y-2.5">
                <li>
                  <Link
                    to="/terms"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <a
                    href="https://trust.morgan.so/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    Trust Center
                  </a>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Social Links */}
            <div>
              <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-white/50">
                Social
              </h3>
              <ul className="space-y-2.5">
                <li>
                  <a
                    href="https://www.linkedin.com/company/morganai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    LinkedIn
                  </a>
                </li>
                {/* Temporarily hidden until the X account is live */}
                {/* <li>
                  <a
                    href="https://x.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/80 transition-colors hover:text-white"
                  >
                    X
                  </a>
                </li> */}
              </ul>
            </div>

            {/* Copyright */}
            <div className="text-sm text-white/60">© 2026</div>
          </div>

          {/* CTA */}
          <div className="flex-shrink-0">
            <Button
              variant="secondary"
              expandIcon
              className="gap-2 bg-[#FCFAF2] text-black hover:bg-[#FCFAF2]/90"
              onClick={() => setIsDemoDialogOpen(true)}
            >
              Book a demo
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Theme switcher + sign-off */}
        <div className="mt-10 flex flex-col gap-4 border-t border-white/20 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <ThemeSwitcherHome />
          <p className="text-sm text-white/60">
            Made with <span aria-label="love">❤️</span> in Tasmania
          </p>
        </div>
      </div>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </footer>
  );
}
